import { useEffect, useRef, useState, type RefObject } from 'react';
import { classNames as cn } from 'primereact/utils';
import { CloseIcon, LoadingSpinner, RedErrorIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { ERROR_STATUS_VALUES, FileStatus } from '@/types/file';
import {
  UPLOAD_FILE_ERROR_LABEL,
  UPLOAD_FILE_ERROR_TOOLTIP_FALLBACK,
  UPLOAD_FILE_STATUS_PROCESSING,
  UPLOAD_FILE_STATUS_UPLOADING,
} from '../constants';
import { FileTypeIcon } from '@/ui/components/FileTypeIcon';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { useColourTheme } from '@/ui/theme/ColourThemeContext';

interface UploadFileButtonType {
  documentId: string;
  documentName: string;
  status?: FileStatus;
  error?: string;
  className?: string;
  itemClassName?: string;
  onRemove?: (documentId: string) => void;
  url?: string;
  onClick?: () => void;
}

function isTruncated(el: HTMLElement | null) {
  return el ? el.scrollWidth - el.clientWidth > 1 : false;
}

/** File chip for the chat input and message attachments. */
export const UploadFileButton = ({
  className = '',
  itemClassName = '',
  documentName,
  documentId,
  status,
  error,
  onRemove,
  url,
  onClick,
}: UploadFileButtonType) => {
  const frostedBgClass = useFrostedGlassClass();
  const { theme } = useColourTheme();
  const errorTintClass = theme === 'dark' ? '' : '!bg-[rgba(255,240,240,0.6)]';
  const nameTargetRef = useRef<HTMLSpanElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  const isUploading = status === FileStatus.UPLOADING;
  const isProcessing = status === FileStatus.CONTENT_EXTRACTION_IN_PROGRESS || status === FileStatus.SCANNING;
  const isActive = isUploading || isProcessing;
  const isFailed = ERROR_STATUS_VALUES.includes(status as FileStatus);
  const hasError = isFailed || !!error;
  const isClickable = Boolean(url || onClick) && !hasError;

  // One-off success/error glow when a file finishes uploading/processing.
  const wasActiveRef = useRef(isActive);
  const [flash, setFlash] = useState<'success' | null>(null);
  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!wasActive || isActive) return;
    // Only success flashes; an error already shows a red border, label and icon.
    if (status !== FileStatus.UPLOADED || hasError) return;
    /* eslint-disable react-hooks/set-state-in-effect -- ephemeral animation flag driven
       by a status transition; setting + timing-out the class is the whole point of the effect. */
    setFlash('success');
    const timer = window.setTimeout(() => setFlash(null), 900);
    /* eslint-enable react-hooks/set-state-in-effect */
    return () => window.clearTimeout(timer);
  }, [isActive, hasError, status]);

  const handleChipClick = () => {
    if (!isClickable) return;
    onClick?.();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const errorText = (error ?? '').trim();
  const statusLabel = isUploading ? UPLOAD_FILE_STATUS_UPLOADING : isProcessing ? UPLOAD_FILE_STATUS_PROCESSING : null;
  const chipTooltipContent =
    isActive && statusLabel
      ? `${documentName} · ${statusLabel}`
      : hasError
        ? `${documentName} · ${errorText || UPLOAD_FILE_ERROR_TOOLTIP_FALLBACK}`
        : documentName;
  // Show the tooltip only when there's something to add (truncated name, in progress or error).
  const shouldShowChipTooltip = () => {
    if (isActive || hasError) return true;
    return isTruncated(nameTargetRef.current);
  };

  const leadingIcon = (
    <span className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <FileTypeIcon fileName={documentName} height={20} className="shrink-0" />
      {isActive && !hasError && (
        <span className="bg-theme-white absolute -right-0.5 -bottom-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full">
          <LoadingSpinner size={10} />
        </span>
      )}
      {hasError && <RedErrorIcon width={10} height={10} className="absolute -right-0.5 -bottom-0.5 overflow-visible" />}
    </span>
  );

  const outerChipClasses = cn(
    'inline-flex h-8 max-w-[min(16.25rem,100%)] min-w-0 cursor-default items-center gap-2 rounded-md border py-1 pl-2.5 text-left transition-colors',
    onRemove ? 'pr-1' : 'pr-2.5',
    hasError
      ? cn('upload-chip-shadow-error border-fletchers-red-200/50 text-theme-black', frostedBgClass, errorTintClass)
      : cn('upload-chip-shadow border-theme-border-grey/50 text-theme-black', frostedBgClass),
    isClickable && 'hover:bg-fletchers-gray-25 cursor-pointer',
    isActive && 'upload-chip-active-border',
    flash === 'success' && 'upload-chip-flash-success',
    className,
    itemClassName,
  );

  const interactiveInnerClasses = cn(
    'flex min-w-0 flex-1 cursor-default items-center gap-2',
    isClickable && 'cursor-pointer',
  );

  const interactiveInner = (
    <>
      {leadingIcon}
      <span ref={nameTargetRef} className="min-w-0 truncate text-[0.8125rem] font-semibold">
        {documentName}
      </span>
      {hasError && (
        <span className="text-theme-text-red shrink-0 text-[0.8125rem] font-semibold">{UPLOAD_FILE_ERROR_LABEL}</span>
      )}
    </>
  );

  return (
    <div ref={chipRef} className={outerChipClasses}>
      {isClickable ? (
        <button type="button" onClick={handleChipClick} aria-label={documentName} className={interactiveInnerClasses}>
          {interactiveInner}
        </button>
      ) : (
        <div className={interactiveInnerClasses}>{interactiveInner}</div>
      )}

      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${documentName}`}
          onClick={() => onRemove(documentId)}
          className={cn(
            'flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors',
            'text-theme-text-grey hover:bg-theme-background-grey hover:text-theme-black',
          )}
        >
          <CloseIcon width={8} height={8} className="shrink-0" />
        </button>
      )}

      {documentName.trim().length > 0 && (
        <Tooltip
          target={chipRef as RefObject<HTMLElement>}
          content={chipTooltipContent}
          position="top"
          className={TOOLTIP_SURFACE}
          baseZIndex={10000}
          onBeforeShow={shouldShowChipTooltip}
        />
      )}
    </div>
  );
};
